import { createContext, createElement, DetailedHTMLProps, forwardRef, InputHTMLAttributes, useContext } from 'react'
import { cn } from '../../lib/utils'

type RadioContextType = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
   name?: string
   inline: boolean
   inputRef?: React.Ref<HTMLInputElement>
}

const RadioContext = createContext<RadioContextType>({} as RadioContextType)

function useRadioContext() {
   const context = useContext(RadioContext)
   if (!context) {
      throw new Error(`Radio components cannot be rendered outside the Radio component`)
   }
   return context
}

type Props = DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement> & {
   children: React.ReactNode
   label?: string
   inline?: boolean
   component?: string | React.ElementType
   className?: string
   labelClassName?: string
}

const RadioComponent = forwardRef<HTMLInputElement, Props>(
   (
      {
         children,
         name,
         label,
         defaultValue,
         inline = false,
         component = 'div',
         className = 'mb-4',
         labelClassName,
         ...rest
      },
      ref,
   ) => {
      return (
         <RadioContext.Provider value={{ name, inline, defaultValue, inputRef: ref, ...rest }}>
            {createElement(component, {
               children: [
                  label ? (
                     <span key={`${name}-label`} className={cn('mb-2 inline-block', labelClassName)}>
                        {label}
                     </span>
                  ) : null,
                  children,
               ],
               className,
            })}
         </RadioContext.Provider>
      )
   },
)

type RadioComponentType = typeof RadioComponent & {
   Option: typeof Option
}

export const Radio = RadioComponent as RadioComponentType

type OptionProps = {
   children: React.ReactNode
   value: string | number
   className?: string
}

const Option = ({ children, value, className }: OptionProps) => {
   const { id, name, inline, inputRef, defaultValue, ...rest } = useRadioContext()
   const labelClassName = cn(
      'mb-2 relative pl-5 cursor-pointer',
      {
         'inline-block ml-4': inline,
         block: !inline,
      },
      className,
   )
   return (
      <label className={labelClassName}>
         <input
            id={id || name}
            type="radio"
            value={value}
            name={name}
            ref={inputRef}
            className="absolute -ml-5 mt-1 w-4 h-4 cursor-pointer"
            {...rest}
         />
         <span className="font-light">{children}</span>
      </label>
   )
}

Radio.Option = Option

export default Radio as RadioComponentType
